import axios from "axios";
import {isEmpty} from "lodash";
import qs from "qs";
import {toast} from "react-toastify";
import {getUrl} from "../constants/urls";
import CookieService from "./cookieService";
import {logout} from "./logout";

const defaultHeaders = {
  "Content-Type": "application/json",
};

const requestProcessV2 = axios.create({
  baseURL: import.meta.env?.VITE_API_BASE_URL,
  headers: defaultHeaders,
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, {arrayFormat: "repeat"}),
  },
});

export default requestProcessV2;

const refreshProcess = axios.create({
  baseURL: import.meta.env?.VITE_API_BASE_URL,
  headers: defaultHeaders,
});

requestProcessV2.interceptors.request.use((req) => {
  const access = CookieService().getAccessToken();
  if (access) {
    req.headers.Authorization = `Bearer ${access || ""}`;
    try {
      if (JSON.parse(localStorage.getItem("customer"))?.id) {
        req.headers["X-CUSTOMER-ID"] = JSON.parse(localStorage.getItem("customer")).id;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Customer not set in local storage");
    }
  }

  return req;
});

requestProcessV2.interceptors.response.use(
  async (response) => {
    if (response?.status === 204) {
      toast.success("Record Deleted Successfully");
    } else {
      // Toaster display on user actions only. Like Add, Edit, Delete
      if (!isEmpty(response?.data?.message) && response?.config?.method !== "get") {
        toast.success(response.data.message);
      }
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 400) {
      const errorData = error?.response?.data;

      if (errorData && typeof errorData === "object") {
        // A request can show some fields itself, for example under the input, and
        // keep them out of the toast with `skipErrorToastFields`.
        const skipFields = error?.config?.skipErrorToastFields || [];
        Object.entries(errorData).forEach(([field, messages]) => {
          if (!messages || skipFields.includes(field)) return;

          const errorMsg = Array.isArray(messages) ? messages[0] : messages;
          if (errorMsg) {
            toast.error(errorMsg);
          }
        });
      } else {
        toast.error("Validation Error");
      }
    } else if (error?.response?.status === 401) {
      try {
        const refreshToken = CookieService().getRefreshToken();
        const rememberMe = CookieService().getRememberMe();
        if (rememberMe && refreshToken) {
          return refreshProcess
            .post(getUrl("refresh"), {refresh: refreshToken})
            .then(({data}) => {
              CookieService().setAppToken(data, false);
              return requestProcessV2.request(error?.config).then((data) => {
                return data;
              });
            })
            .catch(() => {
              toast.error(error?.response?.data?.message);
              logout();
              if (!error?.config?.url?.includes("login")) {
                window.location.reload();
              }
            });
        } else {
          toast.error(error?.response?.data?.message);
          logout();
          if (!error?.config?.url?.includes("login")) {
            window.location.reload();
          }
        }
      } catch (error) {
        toast.error(error?.response?.data?.message);
        logout();
        if (!error?.config?.url?.includes("login")) {
          window.location.reload();
        }
      }
    } else if (error?.response?.status === 500) {
      toast.error("Internal server error occurred, contact your administrator");
    } else {
      // Capture permission error using detail, falling back to the "error"
      // key some endpoints (e.g. clone-sensors job status) use instead, and
      // finally a generic message so a response body we don't recognize
      // never results in an empty toast.
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "Something went wrong. Please try again."
      );
    }

    return Promise.reject(error);
  }
);
